/**
 * A soils map's markups — its "pours": each a numbered group of work areas with one colour and one
 * label ("Over-excavation performed today", "Previous over-excavation") pointing at every area of it.
 * Their labels are known by ids of their own beside the items': '__area' (markup 1), '__area2' …,
 * and '__fail' for the failing areas' label.
 */
import type { Item, MapDoc, WorkArea } from '../../shared/doc.ts';
import { markupOf } from '../../shared/doc.ts';

export const FAIL_LABEL = '__fail';

/** The id of a markup's label. */
export function markupLabelId(n: number): string {
  return n <= 1 ? '__area' : `__area${n}`;
}
/** The markup a label id names (null: not a markup's label). */
export function markupOfLabel(id: string | null | undefined): number | null {
  if (!id) return null;
  if (id === '__area') return 1;
  const m = /^__area(\d{1,4})$/.exec(id);
  return m ? Number(m[1]) : null;
}
/** Is an id one of the shared labels' (a markup's, or the failing areas')? */
export function isSharedLabel(id: string | null | undefined): boolean {
  return id === FAIL_LABEL || markupOfLabel(id) !== null;
}
/** The work areas a shared label points at: a markup's (not failing), or every failing one. */
export function areasOfLabel(items: readonly Item[], id: string): WorkArea[] {
  if (id === FAIL_LABEL) return items.filter((it): it is WorkArea => it.t === 'area' && it.status === 'fail');
  const n = markupOfLabel(id);
  if (n === null) return [];
  return items.filter((it): it is WorkArea => it.t === 'area' && it.status !== 'fail' && markupOf(it) === n);
}
/** A markup's colour, as its areas carry it: its first area's (absent: none drawn yet). */
export function markupColor(items: readonly Item[], n: number): string | null {
  for (const it of items) if (it.t === 'area' && it.status !== 'fail' && markupOf(it) === n) return it.color ?? 'green';
  return null;
}
/** How many markups a map holds (numbered 1 … k, dense). */
export function markupCount(doc: Pick<MapDoc, 'items'>): number {
  let k = 0;
  for (const it of doc.items) if (it.t === 'area' && it.status !== 'fail') k = Math.max(k, markupOf(it));
  return k;
}
